import React, { useState, useEffect } from 'react';
import styles from './EventsShowcase.module.css';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const formatDateParts = (dateString) => {
  if (!dateString) return { day: '', month: '', full: '' };
  
  // Append T00:00:00 to prevent timezone shifting
  const d = new Date(`${dateString}T00:00:00`);
  
  return {
    day: d.toLocaleDateString('en-IN', { day: '2-digit' }),
    month: d.toLocaleDateString('en-IN', { month: 'short' }),
    full: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  };
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const EventsShowcase = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const res = await fetch('/api/event-images');
        
        if (!res.ok) {
          throw new Error('Failed to load events');
        }
        
        const data = await res.json();
        const fetchedEvents = data.events || [];
        
        // 1. Get today's date at midnight for accurate comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 2. Filter out past events
        const upcomingEvents = fetchedEvents.filter((event) => {
          if (!event.date) return false;
          const eventDate = new Date(`${event.date}T00:00:00`);
          return eventDate >= today;
        });

        // 3. (Optional but recommended) Sort events chronologically so the nearest event is first
        upcomingEvents.sort((a, b) => {
          return new Date(`${a.date}T00:00:00`) - new Date(`${b.date}T00:00:00`);
        });

        setEvents(upcomingEvents);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const visibleEvents = showAll ? events : events.slice(0, 4);

  return (
    <section className={styles.section}>
      {/* Decorative background */}
      <div className={styles.backgroundGlow} />
      <div className={styles.backgroundCircle} />

      <div className={styles.container}>
        
        {/* ─────────────────────────────────────
            HEADER
        ───────────────────────────────────── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.eyebrow}>
              <span className={styles.eyebrowLine} />
              WHAT'S ON AT BERLI
            </span>

            <h2 className={styles.heading}>
              Upcoming
              <span> Events</span>
            </h2>
          </div>

          <div className={styles.headerRight}>
            <p>
              Good beer tastes better with good company.
              Join us for live music, special nights and
              unforgettable evenings at Berli.
            </p>
          </div>
        </div>

        {/* ─────────────────────────────────────
            STATES (LOADING / ERROR / EMPTY)
        ───────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'rgba(255,255,255,0.6)' }}>
            Loading upcoming events...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: '#ff6b6b' }}>
            Failed to load events. Please check back later.
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'rgba(255,255,255,0.6)' }}>
            No upcoming events at the moment. Stay tuned!
          </div>
        )}

        {/* ─────────────────────────────────────
            EVENTS GRID
        ───────────────────────────────────── */}
        {!loading && !error && events.length > 0 && (
          <div className={styles.eventsGrid}>
            {visibleEvents.map((event, index) => {
              const dateParts = formatDateParts(event.date);

              return (
                <article
                  className={styles.eventCard}
                  key={event.id || event.fileName}
                  style={{
                    '--delay': `${index * 70}ms`,
                  }}
                >
                  {/* Image */}
                  <div className={styles.imageWrapper}>
                    <img
                      src={event.image}
                      alt={event.eventName}
                      className={styles.eventImage}
                      loading="lazy"
                    />

                    <div className={styles.imageOverlay} />

                    {/* Date */}
                    <div className={styles.dateBadge}>
                      <span className={styles.dateDay}>
                        {dateParts.day}
                      </span>
                      <span className={styles.dateMonth}>
                        {dateParts.month}
                      </span>
                    </div>

                    {/* Top label */}
                    <span className={styles.upcomingLabel}>
                      UPCOMING
                    </span>
                  </div>

                  {/* Content */}
                  <div className={styles.cardContent}>
                    <span className={styles.eventLabel}>
                      EVENT
                    </span>

                    <h3 className={styles.eventName}>
                      {event.eventName}
                    </h3>

                    {event.artistName && (
                      <div className={styles.artist}>
                        <span className={styles.artistLabel}>
                          LIVE BY
                        </span>
                        <span className={styles.artistName}>
                          {event.artistName}
                        </span>
                      </div>
                    )}

                    <div className={styles.cardBottom}>
                      <span className={styles.fullDate}>
                        {dateParts.full}
                      </span>

                      <span className={styles.arrow}>
                        →
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* ─────────────────────────────────────
            VIEW ALL
        ───────────────────────────────────── */}
        {!loading && !error && events.length > 4 && (
          <div className={styles.viewAllWrapper}>
            <button
              type="button"
              className={styles.viewAllButton}
              onClick={() => setShowAll((current) => !current)}
            >
              <span>
                {showAll ? 'Show Less' : 'View All Events'}
              </span>

              <span
                className={`${styles.buttonArrow} ${
                  showAll ? styles.rotated : ''
                }`}
              >
                ↓
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsShowcase;